import React, { useState, useEffect, useRef, useCallback } from "react";
import Button from "react-bootstrap/Button";
import PropTypes from "prop-types";
import downArrowSmall from "svg/downArrowSmall.svg";
import upArrowSmall from "svg/upArrowSmall.svg";
import downArrowLarge from "svg/downArrowLarge.svg";
import upArrowLarge from "svg/upArrowLarge.svg";
import search from "svg/search.svg";
import tick from "svg/tick.svg";
import "./style.scss";

const MultiSelectInput = ({
  label,
  handleSelectInput,
  selectedValues,
  options = [],
  isLaborOptions,
  isStoreOptions,
  isProgStoreOptions,
  defaultValue = "",
  type = "__drop-down",
  inputRef,
  isRequired = false,
  errorMsg,
  isLookup,
  metaData,
  isCloseAfterSelection,
}) => {
  const [displayOptions, setDisplayOptions] = useState(false);
  const filterNode = useCallback((node) => {
    node?.focus();
  }, []);
  const [selectedInput, setSelectedInput] = useState(selectedValues);
  const [dropdownSearchValue, setDropdownSearchValue] = useState("");

  useEffect(() => {
    setSelectedInput(selectedValues);
  }, [selectedValues]);

  useEffect(() => {
    if (isLookup) setDisplayOptions(true);
  }, []);

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  let dropdownOptions = options.filter((ele) => {
    const escapedSearchValue = escapeRegExp(dropdownSearchValue);
    const regex = new RegExp(escapedSearchValue, "i");

    if (isLaborOptions) {
      return ele.laborCode.match(regex);
    }

    if (isLookup) {
      return (ele.laborCode + "-" + ele.description).match(regex);
    }

    if (isStoreOptions || isProgStoreOptions) {
      return (ele.id + "-" + ele.description).match(regex);
    }

    return ele.description.match(regex);
  });

  useEffect(() => {
    let closeDropDownHandler = (event) => {
      if (
        inputRef &&
        inputRef.current &&
        inputRef.current != null &&
        event &&
        event.target &&
        event.target != null &&
        !inputRef.current.contains(event.target)
      ) {
        setDisplayOptions(false);
      }
    };
    document.addEventListener("mousedown", closeDropDownHandler);
    return () => {
      document.removeEventListener("mouse", closeDropDownHandler);
    };
  });

  const handleDisplayOptions = () => {
    setDisplayOptions((current) => !current);
  };

  const handleClick = (element, metaData, e) => {
    handleSelectInput(label, element, metaData, e);
    setDropdownSearchValue("");
    if (isCloseAfterSelection) {
      setDisplayOptions(false);
    }
  };

  const getOptions = () => {
    return (
      dropdownOptions &&
      dropdownOptions.map((ele) => {
        return (
          <div
            className="select__dropdown__list--option"
            key={isLaborOptions ? ele.laborCode : ele.id}
            onClick={(e) =>
              handleClick(
                isLaborOptions
                  ? ele.laborCode
                  : isLookup
                    ? `${ele.laborCode} - ${ele.description}`
                    : isStoreOptions || isProgStoreOptions
                      ? ele.id
                      : ele.description,
                metaData,
                e,
              )
            }
            data-selectedmetadata={JSON.stringify(ele)}
          >
            <img
              src={tick}
              alt={"tick-icon"}
              className={`select__dropdown__tick-icon ${selectedInput.includes(isLaborOptions ? ele.laborCode : isStoreOptions ? ele.id : ele.description) ? "" : "d-none"}`}
            />
            {isLaborOptions
              ? ele.laborCode
              : isLookup
                ? `${ele.laborCode} - ${ele.description}`
                : isStoreOptions || isProgStoreOptions
                  ? `${ele.id} - ${ele.description}`
                  : ele.description}
          </div>
        );
      })
    );
  };

  const dropdownValue = selectedInput?.length == 1 ? selectedInput : `${selectedInput?.length} items selected`;
  const fieldClassName = "select__dropdown__field" + type;
  const inputClassName = fieldClassName + "__input";
  const inputClassNameText = inputClassName + "--text";
  const inputClassNameIcon = fieldClassName + "--icon";
  const downArrowIcon = type === "__pagination" ? downArrowSmall : downArrowLarge;
  const upArrowIcon = type === "__pagination" ? upArrowSmall : upArrowLarge;

  return (
    <div className="form-group select-wrapper">
      {label !== "" && label != null && (
        <div className={`select__label ${isRequired ? "required" : ""}`}>
          <span className="select__label--text">{label}</span>
        </div>
      )}
      <div id="custom-dropdown" ref={inputRef} className="select__dropdown">
        <div className={`${fieldClassName}`}>
          <Button
            variant="outline"
            className={`form-control ${inputClassName} ${errorMsg ? "error-field" : ""}`}
            onClick={handleDisplayOptions}
          >
            <span className={`${inputClassNameText} ${selectedInput?.length == 0 ? "defaultColor" : ""}`}>
              {selectedInput?.length > 0 ? dropdownValue : defaultValue}
            </span>
          </Button>
          <img
            src={displayOptions ? upArrowIcon : downArrowIcon}
            alt={"dropdown-icon"}
            className={inputClassNameIcon}
            onClick={handleDisplayOptions}
          />
        </div>
        {displayOptions && (
          <div className="select__dropdown__list">
            <span className="filter__input__filter">
              <input
                type="text"
                ref={filterNode}
                className="filter__input__filter--input w-100"
                placeholder="Filter"
                value={dropdownSearchValue}
                onChange={(e) => setDropdownSearchValue(e.target.value)}
              />
              <img src={search} alt={"search-icon"} className="filter__input__filter--icon" />
            </span>
            {getOptions()}
          </div>
        )}
      </div>
      {errorMsg && <span className="error-text">{errorMsg}</span>}
    </div>
  );
};

MultiSelectInput.propTypes = {
  label: PropTypes.string,
  defaultValue: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleSelectInput: PropTypes.func.isRequired,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })])
    .isRequired,
};

export default MultiSelectInput;
